import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Filter, Download, Plus, ChevronLeft, ChevronRight, Truck, Package, MapPin, Calendar, Clock, AlertCircle, CheckCircle, Loader2, X, FileText, Trash2, Printer, Eye, FileDown, Mail, Phone } from 'lucide-react';
import orderService from '../services/order.service';
import { sanitizeError } from '../utils/errorUtils';
import { generateInvoice } from '../utils/generateInvoice';

const InternationalOrders = () => {
    const navigate = useNavigate();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filters, setFilters] = useState({
        status: '',
        deliveryPartner: '',
        search: '',
        limit: 50
    });
    const [nimbusBusy, setNimbusBusy] = useState({ orderId: null, action: null });
    const [actionMessage, setActionMessage] = useState(null);
    
    // OTP States
    const [otpModal, setOtpModal] = useState({ show: false, orderId: null, otp: '' });
    const [verifying, setVerifying] = useState(false);

    useEffect(() => {
        fetchOrders();
    }, [filters]);

    const fetchOrders = async () => {
        try {
            setLoading(true);
            setError(null);
            
            const queryParams = {
                orderType: 'international', // Explicitly fetch international orders
                ...filters
            };

            const response = await orderService.getOrders(queryParams);
            
            if (response.success) {
                setOrders(response.data.orders || []);
            }
        } catch (err) {
            setError(sanitizeError(err.response?.data?.message || 'Failed to fetch orders'));
        } finally {
            setLoading(false);
        }
    };

    const handleCreateOrder = () => {
        navigate('/create-order');
    };

    const handleNimbusPickup = async (e, orderId, otp = null) => {
        if (e) e.stopPropagation();
        setActionMessage(null);
        setNimbusBusy({ orderId, action: 'pickup' });
        try {
            const res = await orderService.requestPickup(orderId, otp);
            
            // Check for OTP requirement (sKart 2-step process)
            if (res?.data?.status === 'otp_required' || res?.status === 'otp_required') {
                setOtpModal({ show: true, orderId, otp: '' });
                setActionMessage({ type: 'ok', text: 'OTP has been sent to your mobile. Please verify below.' });
                return;
            }

            // If we have an AWB or success message, it means booking is finalized
            if (res?.success || res?.awb || res?.status === 'confirmed' || res?.message === 'success') {
                setActionMessage({ type: 'ok', text: res.message || 'Shipment booked successfully ✓' });
                setOtpModal({ show: false, orderId: null, otp: '' });
                fetchOrders(); // Refresh to show AWB/Status
            }
        } catch (err) {
            setActionMessage({
                type: 'err',
                text: sanitizeError(err.response?.data?.message || err.message || 'Pickup failed')
            });
        } finally {
            setNimbusBusy({ orderId: null, action: null });
        }
    };

    const handleVerifyOtp = async () => {
        if (!otpModal.otp || otpModal.otp.length < 4) {
            alert('Please enter a valid OTP');
            return;
        }
        setVerifying(true);
        await handleNimbusPickup(null, otpModal.orderId, otpModal.otp);
        setVerifying(false);
    };

    const handleNimbusLabel = async (e, orderId) => {
        e.stopPropagation();
        setActionMessage(null);
        setNimbusBusy({ orderId, action: 'label' });
        try {
            const result = await orderService.downloadLabel(orderId, { inline: true });
            if (result?.url || result?.inline) {
                setActionMessage({ type: 'ok', text: 'Label opened in new tab ✓' });
            } else {
                setActionMessage({ type: 'ok', text: 'Label download started' });
            }
        } catch (err) {
            setActionMessage({
                type: 'err',
                text: sanitizeError(err.message || err.response?.data?.message || 'Label download failed')
            });
        } finally {
            setNimbusBusy({ orderId: null, action: null });
        }
    };

    const handleViewOrder = (orderId) => {
        navigate(`/order-details?id=${orderId}`);
    };

    const handleIntlInvoice = async (e, orderId) => {
        e.stopPropagation();
        setActionMessage(null);
        setNimbusBusy({ orderId, action: 'invoice' });
        try {
            await orderService.downloadInvoice(orderId, { inline: true });
            setActionMessage({ type: 'ok', text: 'Invoice opened in new tab ✓' });
        } catch (err) {
            setActionMessage({
                type: 'err',
                text: sanitizeError(err.message || err.response?.data?.message || 'Invoice download failed')
            });
        } finally {
            setNimbusBusy({ orderId: null, action: null });
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'delivered':
                return <CheckCircle className="w-4 h-4 text-green-600" />;
            case 'picked_up':
            case 'in_transit':
            case 'out_for_delivery':
                return <Truck className="w-4 h-4 text-blue-600" />;
            case 'pending':
            case 'confirmed':
                return <Clock className="w-4 h-4 text-yellow-600" />;
            case 'cancelled':
            case 'rto':
                return <AlertCircle className="w-4 h-4 text-red-600" />;
            default:
                return <Package className="w-4 h-4 text-gray-600" />;
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'delivered':
                return 'bg-green-100 text-green-700';
            case 'picked_up':
            case 'in_transit':
            case 'out_for_delivery':
                return 'bg-blue-100 text-blue-700';
            case 'pending':
            case 'confirmed':
                return 'bg-yellow-100 text-yellow-700';
            case 'cancelled':
            case 'rto':
                return 'bg-red-100 text-red-700';
            default:
                return 'bg-gray-100 text-gray-700';
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    const formatAmount = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2
        }).format(amount || 0);
    };

    // Empty State
    if (!loading && orders.length === 0 && !filters.search && !filters.status) {
        return (
            <div className="flex flex-col items-center justify-center p-8 bg-gray-50 min-h-[calc(100vh-96px)]">
                <div className="text-center">
                    <img src="/images/icon/box-icon.png" alt="box-icon" className='m-auto h-24 w-24 opacity-50'/>
                    <h2 className="mt-9 text-xl font-bold text-gray-800">
                        No International Orders Yet
                    </h2>
                    <p className="mt-1 text-sm text-gray-500 max-w-md mx-auto">
                        Track and manage all your international shipments from this dashboard.
                    </p>
                    <div className="mt-7 flex justify-center">
                        <button
                            onClick={handleCreateOrder}
                            className="flex items-center px-6 py-2.5 text-sm font-semibold text-white bg-blue-600 rounded-lg shadow-md hover:bg-blue-700 transition-all duration-200">
                            <Plus className="w-4 h-4 mr-2" />
                            Create International Order
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Orders List View
    return (
        <div className="min-h-screen font-sans text-[#1a2b4b]">
            {/* Header */}
            <div className="flex justify-between items-center mb-3">
                <h1 className="text-2xl font-bold">International Orders</h1>
                <div className="flex gap-2 items-center">
                    {/* ADDED: Contact phone number */}
                    <a href="tel:+918826885557" className="flex items-center gap-1.5 text-sm font-semibold text-gray-700 hover:text-blue-600 transition">
                        <Phone className="w-4 h-4" />
                        +918826885557
                    </a>

                    {/* ADDED: Email Us button */}
                    <a
                        href="mailto:support@flywelllogistics.com"
                        className="flex items-center px-3 py-1.5 text-sm font-semibold text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition">
                        <Mail className="w-4 h-4 mr-2" />
                        Email Us
                    </a>

                    <button
                        onClick={handleCreateOrder}
                        className="flex items-center px-3 py-1.5 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition">
                        <Plus className="w-4 h-4 mr-2" />
                        Create Order
                    </button>
                </div>
            </div>

            {/* Error Message */}
            {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm flex items-center gap-2">
                    <AlertCircle size={16} />
                    {error}
                </div>
            )}
            {actionMessage && (
                <div className={`mb-4 p-3 border rounded-lg text-sm flex items-center gap-2 ${actionMessage.type === 'err' ? 'bg-red-50 border-red-200 text-red-600' : 'bg-green-50 border-green-200 text-green-600'}`}>
                    {actionMessage.type === 'err' ? <AlertCircle size={16} /> : <CheckCircle size={16} />}
                    <span>{actionMessage.text}</span>
                </div>
            )}

            {/* Filters */}
            <div className="flex flex-wrap gap-2 mb-3">
                <div className="relative flex items-center min-w-[280px] border border-gray-300 rounded-lg bg-white shadow-sm focus-within:ring-2 focus-within:ring-blue-100 transition-all">
                    <Search className="absolute left-3 w-4 h-4 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Search by ID, AWB or customer..."
                        value={filters.search}
                        onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                        className="w-full pl-10 pr-4 py-1.5 text-sm focus:outline-none rounded-lg"
                    />
                </div>

                <select
                    value={filters.status}
                    onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-xs font-bold text-gray-700 bg-white hover:bg-gray-50 transition min-w-[150px]">
                    <option value="">All Status</option>
                    <option value="pending">Pending</option>
                    <option value="confirmed">Confirmed</option>
                    <option value="picked_up">Picked Up</option>
                    <option value="in_transit">In Transit</option>
                    <option value="delivered">Delivered</option>
                    <option value="cancelled">Cancelled</option>
                </select>

                <select
                    value={filters.deliveryPartner}
                    onChange={(e) => setFilters({ ...filters, deliveryPartner: e.target.value })}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-xs font-bold text-gray-700 bg-white hover:bg-gray-50 transition min-w-[150px]">
                    <option value="">All Couriers</option>
                    <option value="skart">International Shipping</option>
                    <option value="delhivery_international">Delhivery International</option>
                </select>
            </div>

            {/* Orders Table */}
            <div className="bg-white rounded-xl overflow-hidden border border-gray-200 shadow-sm">
                {loading ? (
                    <div className="flex flex-col justify-center items-center py-20">
                        <Loader2 className="w-10 h-10 animate-spin text-blue-600 mb-2" />
                        <p className="text-gray-500 text-sm">Loading international orders...</p>
                    </div>
                ) : orders.length === 0 ? (
                    <div className="text-center py-20">
                        <Package className="w-16 h-16 text-gray-200 mx-auto mb-4" />
                        <p className="text-gray-500 font-medium">No orders found matching your criteria</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead className="bg-gray-50 text-[11px] uppercase text-gray-500 font-bold border-b border-gray-200">
                                <tr>
                                    <th className="px-4 py-2">ID / AWB</th>
                                    <th className="px-4 py-2">Customer</th>
                                    <th className="px-4 py-2">Destination</th>
                                    <th className="px-4 py-2">Courier</th>
                                    <th className="px-4 py-2 text-center">Status</th>
                                    <th className="px-4 py-2">Amount</th>
                                    <th className="px-4 py-2 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="text-[13px] divide-y divide-gray-100">
                                {orders.map((order) => (
                                    <tr key={order._id} className="hover:bg-gray-50/50 transition-colors">
                                        <td className="px-4 py-2">
                                            <div className="font-bold text-blue-600">{order.orderNumber}</div>
                                            <div className="text-xs text-gray-400 mt-0.5">{order.awb || 'Label Pending'}</div>
                                        </td>
                                        <td className="px-4 py-2">
                                            <div className="font-medium text-gray-800">{order.deliveryDetails?.name}</div>
                                            <div className="text-xs text-gray-500">{order.deliveryDetails?.email || order.deliveryDetails?.phone}</div>
                                        </td>
                                        <td className="px-4 py-2">
                                            <div className="font-medium text-gray-800">{order.deliveryDetails?.country}</div>
                                            <div className="text-xs text-gray-500">{order.deliveryDetails?.city}</div>
                                        </td>
                                        <td className="px-4 py-2">
                                            <div className="flex items-center gap-2">
                                                <div className="h-2 w-2 rounded-full bg-blue-400"></div>
                                                <span className="capitalize font-medium text-gray-700">
                                                    {order.metadata?.courierName || 
                                                     order.metadata?.nimbusCourierName || 
                                                     order.metadata?.skartCourierName || 
                                                     order.deliveryPartner?.replace(/_/g, ' ')}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-2 text-center">
                                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold ${getStatusColor(order.status)}`}>
                                                {getStatusIcon(order.status)}
                                                {order.status.replace(/_/g, ' ').toUpperCase()}
                                            </span>
                                        </td>
                                        <td className="px-4 py-2 font-bold text-gray-800">
                                            {formatAmount(order.pricing?.totalAmount)}
                                        </td>
                                        <td className="px-4 py-2 text-right">
                                            <div className="flex flex-wrap items-center justify-end gap-2">
                                                <button
                                                    onClick={() => handleViewOrder(order._id)}
                                                    className="inline-flex items-center gap-1 text-xs font-bold text-blue-600 px-2 py-1.5 rounded hover:bg-blue-50 transition"
                                                    title="View Details"
                                                >
                                                    <Eye className="w-3.5 h-3.5" />
                                                    View
                                                </button>
                                                
                                                {/* Pickup Button (Same as Domestic) */}
                                                <button
                                                    type="button"
                                                    onClick={(e) => handleNimbusPickup(e, order._id)}
                                                    disabled={
                                                        (nimbusBusy.orderId === order._id && nimbusBusy.action === 'pickup') ||
                                                        ['cancelled', 'rto', 'picked_up', 'delivered'].includes(order.status)
                                                    }
                                                    className="inline-flex items-center gap-1 text-xs font-bold text-amber-700 border border-amber-200 px-2 py-1.5 rounded hover:bg-amber-50 disabled:opacity-30 disabled:cursor-not-allowed transition"
                                                    title={['cancelled', 'rto'].includes(order.status) ? "Cannot pickup" : "Request pickup / Book shipment"}
                                                >
                                                    {nimbusBusy.orderId === order._id && nimbusBusy.action === 'pickup' ? (
                                                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                                    ) : (
                                                        <Truck className="w-3.5 h-3.5" />
                                                    )}
                                                    Pickup
                                                </button>

                                                {/* Label Button (Same as Domestic) */}
                                                <button
                                                    type="button"
                                                    onClick={(e) => handleNimbusLabel(e, order._id)}
                                                    disabled={
                                                        (nimbusBusy.orderId === order._id && nimbusBusy.action === 'label') ||
                                                        ['cancelled', 'rto', 'pending'].includes(order.status)
                                                    }
                                                    className="inline-flex items-center gap-1 text-xs font-bold text-slate-700 border border-slate-200 px-2 py-1.5 rounded hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed transition"
                                                    title="Download shipping label"
                                                >
                                                    {nimbusBusy.orderId === order._id && nimbusBusy.action === 'label' ? (
                                                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                                    ) : (
                                                        <FileDown className="w-3.5 h-3.5" />
                                                    )}
                                                    Label
                                                </button>

                                                {(order.deliveryPartner === 'delhivery_international' || order.deliveryPartner === 'skart') ? (
                                                    <button
                                                        type="button"
                                                        onClick={(e) => handleIntlInvoice(e, order._id)}
                                                        disabled={
                                                            (nimbusBusy.orderId === order._id && nimbusBusy.action === 'invoice') ||
                                                            !order.awb
                                                        }
                                                        className="inline-flex items-center gap-1 text-xs font-bold text-emerald-700 border border-emerald-200 px-2 py-1.5 rounded hover:bg-emerald-50 disabled:opacity-30 disabled:cursor-not-allowed transition shadow-sm"
                                                        title="Download carrier invoice"
                                                    >
                                                        {nimbusBusy.orderId === order._id && nimbusBusy.action === 'invoice' ? (
                                                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                                        ) : (
                                                            <Printer className="w-3.5 h-3.5" />
                                                        )}
                                                        Invoice
                                                    </button>
                                                ) : (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            generateInvoice(order);
                                                        }}
                                                        className="inline-flex items-center gap-1 text-xs font-bold text-emerald-700 border border-emerald-200 px-2 py-1.5 rounded hover:bg-emerald-50 transition shadow-sm"
                                                        title="Print Flywell Invoice"
                                                    >
                                                        <Printer className="w-3.5 h-3.5" />
                                                        Invoice
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* OTP Modal */}
            {otpModal.show && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 transform transition-all border border-blue-100 relative">
                        {/* Close Button */}
                        <button 
                            onClick={() => {
                                setOtpModal({ show: false, orderId: null, otp: '' });
                                setVerifying(false);
                            }}
                            className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 transition"
                        >
                            <X className="w-5 h-5" />
                        </button>

                        <div className="flex flex-col items-center text-center">
                            <div className="bg-blue-50 p-4 rounded-full mb-4">
                                <Truck className="w-8 h-8 text-blue-600" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-2">Verify Booking OTP</h3>
                            <p className="text-sm text-gray-500 mb-4">
                                sKart has sent a 6-digit verification code to your registered mobile number.
                                Please enter it to finalize your international shipment.
                            </p>
                            
                            {/* Inline Error Message */}
                            {actionMessage && actionMessage.type === 'err' && (
                                <div className="w-full p-3 mb-4 bg-red-50 border border-red-100 rounded-xl text-xs text-red-600 flex items-center gap-2 animate-pulse">
                                    <AlertCircle className="w-4 h-4" />
                                    {actionMessage.text}
                                </div>
                            )}

                            <div className="w-full space-y-4">
                                <input
                                    type="text"
                                    maxLength="6"
                                    placeholder="Enter 6-digit OTP"
                                    value={otpModal.otp}
                                    onChange={(e) => setOtpModal({ ...otpModal, otp: e.target.value })}
                                    className="w-full px-4 py-4 text-center text-2xl font-bold tracking-[0.5em] border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all outline-none"
                                />
                                
                                <div className="flex gap-3">
                                    <button
                                        onClick={() => setOtpModal({ show: false, orderId: null, otp: '' })}
                                        className="flex-1 px-6 py-3 text-sm font-bold text-gray-600 bg-gray-50 rounded-xl hover:bg-gray-100 transition"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleVerifyOtp}
                                        disabled={verifying || otpModal.otp.length < 4}
                                        className="flex-[2] px-6 py-3 text-sm font-bold text-white bg-blue-600 rounded-xl hover:bg-blue-700 disabled:opacity-50 shadow-lg shadow-blue-200 flex items-center justify-center"
                                    >
                                        {verifying ? (
                                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                        ) : (
                                            <CheckCircle className="w-4 h-4 mr-2" />
                                        )}
                                        Verify & Book
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Pagination / Info */}
            {!loading && orders.length > 0 && (
                <div className="mt-4 flex justify-between items-center bg-white p-3 rounded-lg border border-gray-100 shadow-sm">
                    <div className="text-xs text-gray-500 font-medium font-sans">
                        Showing <span className="text-gray-900 font-bold">{orders.length}</span> international shipments
                    </div>
                    <div className="text-[11px] text-blue-500 font-bold uppercase tracking-wider">
                        Real-time backend data
                    </div>
                </div>
            )}
        </div>
    );
};

export default InternationalOrders;